export type CallsIncidentSeverity =
  | 'warning'
  | 'critical';

export type CallsIncidentStatus =
  | 'active'
  | 'resolved';

export type CallsRuntimeIncident = Readonly<{
  id: string;
  key: string;
  owner: 'Calls Governor';
  status: CallsIncidentStatus;
  severity: CallsIncidentSeverity;
  outcome: 'timeout' | 'failure';
  source: string;
  reasonCode: string;
  callSessionId: string | null;
  callId: string | null;
  firstObservedAt: string;
  lastObservedAt: string;
  resolvedAt: string | null;
  occurrences: number;
}>;

export type CallsRuntimeIncidentState = Readonly<{
  active: readonly CallsRuntimeIncident[];
  resolved: readonly CallsRuntimeIncident[];
  totalObserved: number;
}>;

type UnknownRecord = Record<string, unknown>;

const asRecord = (value: unknown): UnknownRecord =>
  value !== null && typeof value === 'object'
    ? (value as UnknownRecord)
    : {};

const asString = (
  value: unknown,
  fallback: string,
): string =>
  typeof value === 'string' && value.trim().length > 0
    ? value
    : fallback;

const asNullableString = (
  value: unknown,
): string | null =>
  typeof value === 'string' && value.trim().length > 0
    ? value
    : null;

const incidentKey = (
  event: UnknownRecord,
): string => {
  const sessionId = asNullableString(
    event.callSessionId,
  );

  if (sessionId) {
    return `session:${sessionId}`;
  }

  const callId = asNullableString(event.callId);

  if (callId) {
    return `call:${callId}`;
  }

  return [
    'signal',
    asString(event.source, 'Calls Governor'),
    asString(event.stage, 'CALLS_RUNTIME_EVENT'),
    asString(
      event.reasonCode,
      'No failure reason recorded',
    ),
  ].join(':');
};

const freezeIncident = (
  incident: CallsRuntimeIncident,
): CallsRuntimeIncident =>
  Object.freeze({ ...incident });

export function buildCallsRuntimeIncidentState(
  values: readonly unknown[],
): CallsRuntimeIncidentState {
  const incidents = new Map<
    string,
    CallsRuntimeIncident
  >();

  for (const value of values) {
    const event = asRecord(value);
    const outcome = asString(
      event.outcome,
      'unknown',
    );

    const key = incidentKey(event);
    const observedAt = asString(
      event.observedAt,
      'Unknown',
    );

    if (outcome === 'recovery') {
      const existing = incidents.get(key);

      if (existing?.status === 'active') {
        incidents.set(
          key,
          freezeIncident({
            ...existing,
            status: 'resolved',
            lastObservedAt: observedAt,
            resolvedAt: observedAt,
          }),
        );
      }

      continue;
    }

    if (
      outcome !== 'timeout' &&
      outcome !== 'failure'
    ) {
      continue;
    }

    const existing = incidents.get(key);

    if (existing?.status === 'active') {
      incidents.set(
        key,
        freezeIncident({
          ...existing,
          severity:
            outcome === 'failure'
              ? 'critical'
              : existing.severity,
          outcome,
          reasonCode: asString(
            event.reasonCode,
            existing.reasonCode,
          ),
          lastObservedAt: observedAt,
          occurrences: existing.occurrences + 1,
        }),
      );

      continue;
    }

    incidents.set(
      key,
      freezeIncident({
        id: `calls-incident:${key}`,
        key,
        owner: 'Calls Governor',
        status: 'active',
        severity:
          outcome === 'failure'
            ? 'critical'
            : 'warning',
        outcome,
        source: asString(
          event.source,
          'Calls Governor',
        ),
        reasonCode: asString(
          event.reasonCode,
          'No failure reason recorded',
        ),
        callSessionId: asNullableString(
          event.callSessionId,
        ),
        callId: asNullableString(
          event.callId,
        ),
        firstObservedAt: observedAt,
        lastObservedAt: observedAt,
        resolvedAt: null,
        occurrences: 1,
      }),
    );
  }

  const all = [...incidents.values()];

  return Object.freeze({
    active: Object.freeze(
      all
        .filter(
          incident =>
            incident.status === 'active',
        )
        .map(freezeIncident),
    ),
    resolved: Object.freeze(
      all
        .filter(
          incident =>
            incident.status === 'resolved',
        )
        .map(freezeIncident),
    ),
    totalObserved: values.length,
  });
}
